import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, catchError, EMPTY, finalize, mergeMap, switchMap, tap } from 'rxjs';
import { ModalComponent, ModalData } from 'shared/src/components/modal/modal.component';
import { ApiService, Placement } from 'shared/src/services/api.service';
import { UserInfoService } from 'shared/src/services/user-info.service';

export interface UpsellOffer {
  title: string;
  subtitle?: string;
  description: string;
  cta: string;
  imageClass?: string;
  // CSS custom properties (--upsell-img / --upsell-img-dark) resolved per locale by the
  // flow component; the stylesheet only consumes the variables.
  imageStyle?: Record<string, string>;
  addonCode: string;
  colorScheme: 'green' | 'orange' | 'blue';
  priceLabel: string;
  fullPriceLabel: string;
  discountLabel: string;
  placement: Placement;
  value: number;
  currency: string;
}

@Component({
  selector: 'app-upsell',
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './upsell.component.html',
  styleUrl: './upsell.component.scss',
})
export class UpsellComponent implements OnInit {
  @Input() offer!: UpsellOffer;

  @Output() onAction = new EventEmitter<{
    action: 'continue' | 'skip' | 'error';
    offer: UpsellOffer;
  }>();

  isPurchasing = false;

  private purchaseTrigger$ = new BehaviorSubject<void | null>(null);

  constructor(
    private apiService: ApiService,
    private userInfo: UserInfoService,
    private dialog: MatDialog,
    @Inject(LOCALE_ID) private localeId: string
  ) {}

  ngOnInit(): void {
    this.setupPurchaseStream();
  }

  private setupPurchaseStream(): void {
    // No takeUntilDestroyed: the addon charge must settle and report its result even if the
    // user leaves mid-request, like the other payment submissions.
    this.purchaseTrigger$
      .pipe(
        switchMap(trigger => {
          if (trigger === null) return EMPTY;

          this.isPurchasing = true;

          return this.apiService
            .buyStripeUpsell({
              addonCode: this.offer.addonCode,
              email: this.userInfo.getEmail(),
              stripeCustomerId: this.userInfo.getStripeCustomerId(),
              placement: this.offer.placement,
              language: this.getNormalizedLocale(),
            })
            .pipe(
              mergeMap(() =>
                this.dialog
                  .open<ModalComponent, ModalData>(ModalComponent, {
                    disableClose: false,
                    data: {
                      title: $localize`:@@upsellCongratsTitle:Congratulations!`,
                      message:
                        $localize`:@@upsellPurchasedMessagePrefix:You have purchased ` +
                        this.stripTrailingPeriod(this.offer.title) +
                        $localize`:@@upsellPurchasedMessageSuffix:. You will see it in your profile once you get the app.`,
                      rive: {
                        fileName: 'success',
                        animations: [],
                        ratio: 16 / 9,
                      },
                    },
                  })
                  .afterClosed()
              ),
              tap(() => {
                this.onAction.emit({
                  action: 'continue',
                  offer: this.offer,
                });
              }),
              catchError(error => {
                console.error('Failed to purchase upsell:', error);
                return this.dialog
                  .open<ModalComponent, ModalData>(ModalComponent, {
                    disableClose: false,
                    data: {
                      title: $localize`:@@upsellSorryTitle:Sorry`,
                      message: $localize`:@@upsellPurchaseFailedMessage:We were unable to purchase the addon. Please contact our support team - support@getpeech.com`,
                    },
                  })
                  .afterClosed()
                  .pipe(
                    tap(() => {
                      this.onAction.emit({ action: 'error', offer: this.offer });
                    })
                  );
              }),
              finalize(() => {
                this.isPurchasing = false;
              })
            );
        })
      )
      .subscribe();
  }

  onContinue(): void {
    if (this.isPurchasing) return;

    this.purchaseTrigger$.next(undefined);
  }

  onSkip(): void {
    if (this.isPurchasing) return;

    this.onAction.emit({ action: 'skip', offer: this.offer });
  }

  private getNormalizedLocale(): string {
    return (this.localeId || '').trim().replace('_', '-').toLowerCase().split('-')[0];
  }

  // Avoids a double period where the suffix below also starts with one.
  private stripTrailingPeriod(title: string): string {
    return title.replace(/\.(\s*(?:<\/[^>]+>)*)\s*$/, '$1');
  }
}
